import axios from 'axios';

const API_BASE = '/api/v1';

export interface CreateSessionResponse {
  id: string;
  caseNumber: string;
  expiresAt: string;
}

export interface PresignedURLResponse {
  uploadUrl: string;
  fileId: string;
  headers: Record<string, string>;
}

/**
 * Compute a hex-encoded SHA-256 over an ArrayBuffer using the WebCrypto API.
 * Returns the 64-character lowercase hex string, or null if the API is unavailable.
 */
export async function computeSHA256Hex(data: ArrayBuffer): Promise<string | null> {
  if (!crypto?.subtle) {
    return null;
  }
  const hashBuf = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(hashBuf);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export const uploadAPI = {
  createSession: async (caseNumber: string) => {
    return axios.post<CreateSessionResponse>(`${API_BASE}/upload/session`, {
      caseNumber,
    });
  },

  getPresignedURL: async (sessionId: string, fileName: string, fileSize: number) => {
    return axios.post<PresignedURLResponse>(`${API_BASE}/upload/presigned`, {
      sessionId,
      fileName,
      fileSize,
    });
  },

  /**
   * Complete an upload. Optionally sends the plaintext SHA-256 so the server
   * can persist it to File.SHA256Hash (supportlogsio-1-187).
   * The hash must be computed over the *plaintext* bytes before any encryption.
   */
  completeUpload: async (sessionId: string, fileId: string, sha256?: string) => {
    return axios.post(`${API_BASE}/upload/complete`, {
      sessionId,
      fileId,
      ...(sha256 ? { sha256 } : {}),
    });
  },

  uploadFile: async (
    url: string,
    file: File | ArrayBuffer,
    headers: Record<string, string>,
    onProgress?: (progress: number) => void
  ) => {
    const data = file instanceof File ? await file.arrayBuffer() : file;

    return axios.put(url, data, {
      headers: {
        'Content-Type': 'application/octet-stream',
        ...headers,
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  },
};
